import { onBeforeUnmount } from "@vue/composition-api";

/**
 * Attaches a document-level click listener that fires `onOutside` when the
 * click lands outside `targetRef`. Listener is opt-in (call `enable(cb)` to
 * start, `disable()` to stop). Auto-cleanup on component unmount.
 *
 * Use case: dropdowns/flyouts where the open/close lifecycle is controlled
 * by the consumer (we don't want a permanent listener; only while open).
 *
 * @param {import("@vue/composition-api").Ref<HTMLElement|null>} targetRef
 * @param {Object} [options]
 * @param {string} [options.exclude] CSS selector. Clicks on elements matching
 *   this selector (or their descendants) are ignored — useful for the trigger
 *   button that opens the popover, so its own click handler stays in charge.
 */
export function useClickOutside(targetRef, options = {}) {
    const { exclude = "" } = options;
    let onOutside = null;
    let listening = false;

    function handler(event) {
        const target = targetRef.value;
        if (!target) return;
        if (target.contains(event.target)) return;
        if (exclude && event.target.closest && event.target.closest(exclude)) {
            return;
        }
        if (onOutside) onOutside(event);
    }

    function enable(cb) {
        onOutside = cb;
        if (listening) return;
        // setTimeout 0: el click que dispara enable() todavía está bubble-ando
        // hacia document. Si lo atrapamos en este tick, cerramos al toque.
        setTimeout(() => {
            document.addEventListener("click", handler);
            listening = true;
        }, 0);
    }

    function disable() {
        document.removeEventListener("click", handler);
        listening = false;
        onOutside = null;
    }

    onBeforeUnmount(() => {
        disable();
    });

    return { enable, disable };
}
